import {
  fetchPipelineCard,
  fetchPipelineDrilldown,
  fetchPipelineTableAndChart,
} from './actions';
import { getDefaultAtsFilters, buildReportsSlice } from '../common/ats-filters';
import { flatTransformer } from '../common/flat-transformer';
import { cardTransformer } from '../common/card-transformer';
import { getPercentage } from '../common/get-percentage';

const getChartData = (response) => {
  const {
    chart = [],
    headers,
    lookups: { dt, g1, g2 } = {},
  } = response?.data || {};
  return flatTransformer({ headers, datas: chart, g1, g2, dt });
};

const getDynamicPercentage = (datum, dynamic_total) => {
  const result = {};
  for (let i = 0; i < 15; i++) {
    result[`s${i + 1}_per`] = getPercentage(
      datum[`s${i + 1}_total_count`],
      dynamic_total[i]
    );
  }
  return result;
};

const singleJobTableWithTotal = ({ data = [] }) => {
  const initialTotal = Array(15).fill(0);

  const dynamic_total = data?.reduce((acc, curr) => {
    for (let i = 0; i < initialTotal?.length; i++) {
      acc[i] = acc[i] + curr[`s${i + 1}_total_count`];
    }
    return acc;
  }, initialTotal);

  function innerFunc(data) {
    if (!data?.length) return [];
    return data.map((datum) => {
      const percentage = getDynamicPercentage(datum, dynamic_total);
      return {
        ...datum,
        dynamic_total,
        ...percentage,
        subRows: innerFunc(datum?.subRows),
      };
    });
  }

  return innerFunc(data);
};

const tableWithTotal = ({ data = [], misc = {} }) => {
  if (misc) {
    return singleJobTableWithTotal({ data, misc });
  }

  const application_review_total_count_total = data?.reduce(
    (acc, curr) => acc + curr?.application_review_total_count,
    0
  );
  const interviews_total_count_total = data?.reduce(
    (acc, curr) => acc + curr?.interviews_total_count,
    0
  );
  const offer_total_count_total = data?.reduce(
    (acc, curr) => acc + curr?.offer_total_count,
    0
  );
  const hired_total_count_total = data?.reduce(
    (acc, curr) => acc + curr?.hired_total_count,
    0
  );

  function innerFunc(data) {
    if (!data?.length) return [];
    return data.map((datum) => {
      return {
        ...datum,

        application_review_total_count_total,
        application_review_total_count_per: getPercentage(
          datum?.application_review_total_count,
          application_review_total_count_total
        ),

        interviews_total_count_total,
        interviews_total_count_total_per: getPercentage(
          datum?.interviews_total_count,
          interviews_total_count_total
        ),
        offer_total_count_total,
        offer_total_count_per: getPercentage(
          datum?.offer_total_count,
          offer_total_count_total
        ),
        hired_total_count_total,
        hired_total_count_per: getPercentage(
          datum?.hired_total_count,
          hired_total_count_total
        ),

        subRows: innerFunc(datum?.subRows),
      };
    });
  }

  return innerFunc(data);
};

const getTableData = (response, payload) => {
  const {
    chart = [],
    table = [],
    headers,
    lookups: { dt, g1, g2 } = {},
    misc,
  } = response?.data;

  const { group: { group_by_1, group_by_2 } = {} } = payload || {};

  const datas = g1?.length || g2?.length ? table : chart;
  const headerKey = g1?.length ? 'g1' : 'dt';
  return {
    data: flatTransformer({
      headers,
      datas,
      g1,
      g2,
      dt,
      headerKey,
      g1Key: group_by_1?.id,
      g2Key: group_by_2?.id,
    }),
    misc: misc,
  };
};

const constructDrilldownData = (payload, drilldown) => {
  const k1Map = payload?.data?.lookups?.k1;
  const k2Map = payload?.data?.lookups?.k2;
  const k3Map = payload?.data?.lookups?.k3;
  const k4Map = payload?.data?.lookups?.k4;
  const k5Map = payload?.data?.lookups?.k5;
  const k6Map = payload?.data?.lookups?.k6;
  const k7Map = payload?.data?.lookups?.k7;
  const returnData = payload?.data?.table?.map((data) => {
    // eslint-disable-next-line no-unused-vars
    const [
      ,
      k1,
      k2,
      k3,
      k4,
      k5,
      k6,
      k7,
      stage_name,
      department_name,
      office_names,
      workplace,
      work_type,
    ] = data;
    if (drilldown?.metric === 'interviewed') {
      return {
        ...k1Map[k1],
        credited_to: k2Map[k2],
        source: k3Map[k3],
        job: k4Map[k4],
        created_by: k5Map[k5],
        recruiters: k6Map[k6],
        hiring_managers: k7Map[k7],
        stage_name,
        department_name,
        office_names,
        workplace,
        work_type,
      };
    }

    if (drilldown?.metric === 'offer') {
      return {
        ...k1Map[k1],
        credited_to: k2Map[k2],
        source: k3Map[k3],
        job: k4Map[k4],
        created_by: k5Map[k5],
        recruiters: k6Map[k6],
        hiring_managers: k7Map[k7],
        stage_name,
        department_name,
        office_names,
        workplace,
        work_type,
      };
    }

    return {
      ...k1Map[k1],
      credited_to: k2Map[k2],
      source: k3Map[k3],
      job: k4Map[k4],
      created_by: k5Map[k5],
      recruiters: k6Map[k6],
      hiring_managers: k7Map[k7],
      stage_name,
      department_name,
      office_names,
      workplace,
      work_type,
    };
  });

  return returnData;
};

const initialState = {
  ...getDefaultAtsFilters(),

  group: {
    group_by_1: {
      id: null,
      label: 'Time',
    },
    group_by_2: null,
  },

  isCardLoading: false,
  cards: {},

  chart: [],
  table: [],
  isChartLoading: false,
  isTableLoading: false,
  isDrillLoading: false,
  initialLoading: false,
  drilldownData: [],
  stageMapping: {},
  totalCount: 0,
  currentDrillPage: 1,
};

const extraReducers = {
  [fetchPipelineCard.pending]: (state) => {
    state.isCardLoading = true;
  },
  [fetchPipelineCard.fulfilled]: (state, { payload }) => {
    state.cards = cardTransformer(payload);
    state.isCardLoading = false;
  },
  [fetchPipelineCard.rejected]: (state) => {
    state.isCardLoading = false;
  },

  [fetchPipelineTableAndChart.pending]: (state) => {
    state.isChartLoading = true;
    state.isTableLoading = true;
    state.stageMapping = {};
  },

  [fetchPipelineTableAndChart.fulfilled]: (state, { meta, payload = {} }) => {
    state.isChartLoading = false;
    state.isTableLoading = false;
    state.chart = getChartData(payload);
    state.table = tableWithTotal(getTableData(payload, meta?.arg));
    state.stageMapping = payload?.data?.misc?.stage_mapping || {};
  },

  [fetchPipelineTableAndChart.rejected]: (state) => {
    state.isChartLoading = false;
    state.isTableLoading = false;
  },

  [fetchPipelineDrilldown.pending]: (state, { meta }) => {
    const { drilldown } = meta?.arg || {};
    if (drilldown.page === 1) {
      state.initialLoading = true;
    } else {
      state.isDrillLoading = true;
    }
  },
  [fetchPipelineDrilldown.fulfilled]: (state, { meta, payload = {} }) => {
    if (!payload) {
      return;
    }

    state.isDrillLoading = false;
    state.initialLoading = false;
    const { drilldown } = meta?.arg || {};
    const newData = constructDrilldownData(payload, drilldown);
    state.totalCount = payload?.data?.pagination?.total_count;

    if (drilldown.page === 1) {
      state.drilldownData = newData;
    } else {
      state.drilldownData = [...state.drilldownData, ...newData];
    }
    state.currentDrillPage = drilldown.page;
  },
  [fetchPipelineDrilldown.rejected]: (state, action) => {
    if (
      action.error?.name === 'AbortError' ||
      action.error?.name === 'CanceledError'
    ) {
      return;
    }

    state.isDrillLoading = false;
    state.initialLoading = false;
  },
};

export const { reducer, actions } = buildReportsSlice({
  name: 'atsPipelineReport',
  initialState,
  extraReducers,
});

export const {
  applyFilter,
  setFiltersData,
  resetFilter,
  applyTimeFrame,
  applyViewBy,
  applyGroup,
  applySearch,
  resetDrillDown,
} = actions;
export default reducer;
